import ActivateLoop from "@/components/call/access-controls/activate-loop";
import CloseGate from "@/components/call/access-controls/close-gate";
import { getGateActions } from "@/components/call/access-controls/gate-actions";
import GateStatus from "@/components/call/access-controls/gate-status";
import { getSharedGateNames } from "@/components/call/access-controls/gate-status.utils";
import OpenGate from "@/components/call/access-controls/open-gate";
import OpenLockedGate from "@/components/call/access-controls/open-locked-gate";
import SendLostTicket from "@/components/call/access-controls/send-lost-ticket";
import { AccessControlGateStatus } from "@EspaceParkQC/sdk-call-center";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@EspaceParkQC/sdk-react";
import type { CallCenterV3Types } from "@EspaceParkQC/sdk-services-legacy";
import { Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

interface IntercomAccessControlsProps {
  /** The intercom the call came from: its extension (and gate) and the park, with every extension at the park. */
  intercomPartyDetails: CallCenterV3Types.CallPartyParkingIntercom;
}

export default function IntercomAccessControls({ intercomPartyDetails }: IntercomAccessControlsProps): React.ReactElement {
  const { t } = useTranslation("main");
  const { extension, parkData } = intercomPartyDetails;
  const gateKey = `${parkData.id}:${extension.gate?.deviceId}:${extension.gate?.gateId}`;
  const sharedWith = getSharedGateNames(extension.gate, parkData.intercomExtensions);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="size-5" />
          {t("intercom.access-controls.title")}
        </CardTitle>
        <CardDescription>{t("intercom.access-controls.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <AccessControlGateStatus
          render={(gate) => {
            const actions = getGateActions({ isHeld: gate.isHeld });
            return (
              <>
                <GateStatus {...gate} gateKey={gateKey} sharedWith={sharedWith} />
                {/* Keyed so Close keeps its open dialog when it moves to the front as the gate becomes held. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {actions.close && actions.closeVariant === "primary" && <CloseGate key="close" variant="primary" />}
                  {actions.open && <OpenGate key="open" />}
                  {actions.close && actions.closeVariant !== "primary" && <CloseGate key="close" variant="default" />}
                  <SendLostTicket key="lost-ticket" />
                  <ActivateLoop key="loop" />
                  {actions.holdOpen && <OpenLockedGate key="hold-open" gateName={gate.gateName} />}
                </div>
              </>
            );
          }}
        />
      </CardContent>
    </Card>
  );
}
